import { useState, useEffect, useCallback, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useLocation } from 'react-router-dom'
import { api } from '@/services/api'
import { useSubscription } from '@/hooks/useSubscription'
import { Content } from '@/services/homepage'
import { getAllProviderVideos, normalizeEpornerVideo, normalizeLetsJerktvVideo, normalizeRedtubeVideo } from '@/services/adultContent'
import { buildSubscriptionUrl } from '@/utils/subscriptionRedirect'
import './DiscoverPage.css'

interface Tag {
  id: string
  name: string
}

// Tags populaires avec leurs niveaux requis (défini avant le composant pour être accessible)
const allTags: Tag[] = [
  { id: 'milf', name: 'Milf' },
  { id: 'teens', name: 'Teens' },
  { id: 'asian', name: 'Asian' },
  { id: 'anal', name: 'Anal' },
  { id: 'lesbian', name: 'Lesbian' },
  { id: 'blowjob', name: 'Blowjob' },
  { id: 'hardcore', name: 'Hardcore' },
  { id: 'amateur', name: 'Amateur' },
  { id: 'solo', name: 'Solo' },
  { id: 'gangbang', name: 'Gangbang' },
  { id: 'bbc', name: 'BBC' },
  { id: 'dp', name: 'DP' }
]

const DiscoverPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { hasAccessLevel, getActiveAccessLevel } = useSubscription()
  
  // Récupérer le tag depuis le state de navigation (pas depuis l'URL)
  const filterTagFromState = (location.state as { filterTag?: string })?.filterTag || null
  
  // Initialiser selectedTags avec le tag du state si présent et valide
  const isTagInList = (tagName: string): boolean => {
    return allTags.some(tag => tag.id.toLowerCase() === tagName.toLowerCase() || tag.name.toLowerCase() === tagName.toLowerCase())
  }
  
  const initialTags = filterTagFromState && isTagInList(filterTagFromState) 
    ? [allTags.find(tag => tag.id.toLowerCase() === filterTagFromState.toLowerCase() || tag.name.toLowerCase() === filterTagFromState.toLowerCase())?.id || filterTagFromState.toLowerCase()].filter(Boolean) as string[]
    : []
  
  const [selectedTags, setSelectedTags] = useState<string[]>(initialTags)
  const [videos, setVideos] = useState<Content[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const loadingRef = useRef(false)
  
  // Pages actuelles pour chaque provider (utilisons des refs pour éviter les dépendances)
  const epornerPageRef = useRef(1)
  const letsjerktvPageRef = useRef(1)
  const redtubePageRef = useRef(1)

  // Configuration des accès aux tags par niveau
  const tagAccessConfig = {
    basic: ['amateur'],
    premium: ['amateur', 'milf', 'teens', 'asian', 'solo', 'lesbian'],
    vip: ['amateur', 'milf', 'teens', 'asian', 'solo', 'lesbian', 'anal', 'blowjob', 'hardcore', 'gangbang', 'bbc', 'dp']
  }


  // Déterminer si un tag est accessible à l'utilisateur
  const isTagAccessible = useCallback((tagId: string): boolean => {
    const accessLevel = getActiveAccessLevel()
    if (!accessLevel) {
      // Sans abonnement, accès uniquement à la première page sans tags
      return false
    }
    return tagAccessConfig[accessLevel]?.includes(tagId) || false
  }, [getActiveAccessLevel])

  // Obtenir le niveau requis pour un tag
  const getTagRequiredLevel = useCallback((tagId: string): 'basic' | 'premium' | 'vip' | null => {
    for (const [level, tags] of Object.entries(tagAccessConfig)) {
      if (tags.includes(tagId)) {
        return level as 'basic' | 'premium' | 'vip'
      }
    }
    return null
  }, [])

  // Fonction pour charger les vidéos depuis les providers
  const loadVideos = useCallback(async (isInitialLoad = false) => {
    if (loadingRef.current) return
    
    // Vérifier si l'utilisateur peut charger plus (limité à 1 page pour basic ou sans abonnement)
    const accessLevel = getActiveAccessLevel()
    const canLoadMorePages = accessLevel !== null && accessLevel !== 'basic'
    
    if (!isInitialLoad && !canLoadMorePages) {
      return
    }
    
    loadingRef.current = true
    setIsLoading(true)

    try {
      let newVideos: Content[] = []
      
      if (selectedTags.length === 0) {
        // Chargement initial sans tags
        if (isInitialLoad) {
          const providerVideos = await getAllProviderVideos()
          newVideos = [
            ...providerVideos.eporner,
            ...providerVideos.letsjerktv,
            ...providerVideos.redtube
          ]
        }
      } else {
        // Chargement avec tags sélectionnés
        const searchTerm = selectedTags.join(' ')
        
        // Sauvegarder les pages actuelles avant d'incrémenter
        const currentEpornerPage = epornerPageRef.current
        const currentLetsjerktvPage = letsjerktvPageRef.current
        const currentRedtubePage = redtubePageRef.current
        
        const promises = [
          // Eporner
          api.get('/adult-content/eporner/search', {
            params: {
              query: searchTerm,
              per_page: 10,
              page: currentEpornerPage,
              order: 'latest'
            }
          }).then(res => {
            const videos = res.data?.videos || []
            return {
              videos: videos.map((v: any, idx: number) => normalizeEpornerVideo(v, idx)),
              hasMore: videos.length === 10
            }
          }),
          
          // LetsJerktv
          api.get('/adult-content/letsjerktv/search', {
            params: {
              query: searchTerm,
              page: currentLetsjerktvPage
            }
          }).then(res => {
            const videos = res.data?.videos || []
            return {
              videos: videos.map((v: any, idx: number) => normalizeLetsJerktvVideo(v, idx)),
              hasMore: videos.length > 0
            }
          }),
          
          // Redtube - passer les tags comme un tableau avec la syntaxe tags[]
          (() => {
            // Construire les paramètres pour utiliser tags[]=tag1&tags[]=tag2
            const redtubeParams: Record<string, any> = {
              page: currentRedtubePage
            }
            // Stocker les tags séparément pour le serializer
            const redtubeTags = selectedTags
            
            // Utiliser un serializer personnalisé pour gérer tags[]
            return api.get('/adult-content/redtube/search', {
              params: redtubeParams,
              paramsSerializer: (params: Record<string, any>) => {
                const parts: string[] = []
                // Ajouter page
                parts.push(`page=${encodeURIComponent(params.page)}`)
                // Ajouter tous les tags avec tags[]=value
                redtubeTags.forEach(tag => {
                  parts.push(`tags[]=${encodeURIComponent(tag)}`)
                })
                return parts.join('&')
              }
            }).then(res => {
              const videos = res.data?.videos || []
              return {
                videos: videos.map((v: any, idx: number) => normalizeRedtubeVideo(v, idx)),
                hasMore: videos.length > 0
              }
            })
          })()
        ]
        
        const results = await Promise.allSettled(promises)
        
        const allVideos: Content[] = []
        let hasMoreResults = false
        
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            allVideos.push(...result.value.videos)
            if (result.value.hasMore) {
              hasMoreResults = true
            }
          }
        })
        
        // Retirer les doublons
        const uniqueVideos = Array.from(
          new Map(allVideos.map((v: any) => [v?.id, v])).values()
        ).filter((v): v is Content => v !== null)
        
        newVideos = uniqueVideos
        setHasMore(hasMoreResults)
        
        // Incrémenter les pages uniquement si on a reçu des résultats
        if (uniqueVideos.length > 0) {
          epornerPageRef.current += 1
          letsjerktvPageRef.current += 1
          redtubePageRef.current += 1
        }
      }
      
      setVideos(prev => [...prev, ...newVideos])
    } catch (error) {
      console.error('Erreur lors du chargement des vidéos:', error)
    } finally {
      setIsLoading(false)
      loadingRef.current = false
    }
  }, [selectedTags])

  // Nettoyer le state de navigation après lecture
  useEffect(() => {
    if (location.state && (location.state as { filterTag?: string }).filterTag) {
      window.history.replaceState({}, '', location.pathname)
    }
  }, [])

  // Chargement initial et lorsque les tags changent
  useEffect(() => {
    setVideos([])
    epornerPageRef.current = 1
    letsjerktvPageRef.current = 1
    redtubePageRef.current = 1
    setHasMore(true)
    // Si on a un tag initial depuis le state, charger directement les résultats filtrés
    // Sinon, charger les données de base
    loadVideos(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTags])

  const getVideoThumbnailUrl = (video: Content): string | undefined => {
    if (!video?.thumbnailS3Key || typeof video.thumbnailS3Key !== 'string') {
      return undefined
    }

    return video.thumbnailS3Key.startsWith('http')
      ? video.thumbnailS3Key
      : `/api/contents/${video.id}/thumbnail`
  }

  const buildVideoRedirectUrl = (video: Content): string => {
    const primaryTagId = selectedTags[0]
    const primaryTag = primaryTagId ? allTags.find((tag) => tag.id === primaryTagId) : undefined

    return buildSubscriptionUrl({
      from: 'video',
      videoId: String(video.id),
      videoTitle: video.title,
      videoThumbnail: getVideoThumbnailUrl(video),
      videoProvider: video.provider,
      tagId: primaryTagId,
      tagName: primaryTag?.name,
    })
  }

  const toggleTag = (tagId: string) => {
    // Vérifier l'accès avant de sélectionner le tag
    if (!isTagAccessible(tagId)) {
      const requiredLevel = getTagRequiredLevel(tagId)
      if (requiredLevel) {
        const tag = allTags.find((t) => t.id === tagId)
        navigate(
          buildSubscriptionUrl({
            from: 'tag',
            tagId,
            tagName: tag?.name || tagId,
          })
        )
      }
      return
    }
    
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    )
  }

  // Vérifier si le Basic peut continuer à charger (limité à 1 page)
  const accessLevel = getActiveAccessLevel()
  const canLoadMore = accessLevel === null || accessLevel === 'basic' ? false : hasMore

  // Observer pour détecter le scroll en bas de page
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    if (loadingRef.current) return
    if (observerRef.current) observerRef.current.disconnect()
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
        loadVideos()
      }
    })
    
    if (node) observerRef.current.observe(node)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore])

  // Cleanup de l'observer
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  const handleVideoClick = (video: Content, videoIndex: number) => {
    if (!video?.id) {
      console.warn('Impossible de naviguer : vidéo invalide', { video })
      return
    }

    // Les 3 premières vidéos sont gratuites (basic)
    const isFirstVideos = videoIndex < 3
    // Pour le reste, vérifier si l'utilisateur a au moins le niveau premium
    const hasPremiumAccess = hasAccessLevel('premium')
    const hasVipAccess = hasAccessLevel('vip')
    const hasAccess = isFirstVideos || hasVipAccess || (videoIndex >= 3 && hasPremiumAccess)

    if (hasAccess) {
      navigate(`/content/${video.id}`)
    } else {
      navigate(buildVideoRedirectUrl(video))
    }
  }

  const canonicalUrl = `https://www.edenplaisir.com${location.pathname}`.replace(/\/$/, '').split('?')[0] || 'https://www.edenplaisir.com/discover'

  return (
    <div className="discover-page">
      <Helmet>
        <title>Découvrir - EdenPlaisir.com</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content="Explorez notre contenu organisé par tags et catégories sur EdenPlaisir.com. Trouvez facilement les vidéos qui correspondent à vos préférences." />
        <meta name="keywords" content="découvrir, recherche, tags, catégories, vidéos, EdenPlaisir" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Découvrir - EdenPlaisir.com" />
        <meta property="og:description" content="Explorez notre contenu organisé par tags et catégories." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      
      {/* Header */}
      <div className="discover-header">
        <h1>Découvrir</h1>
        <p>Explorez notre contenu organisé par tags et catégories</p>
      </div>

      <div className="discover-content">
        {/* Sidebar avec filtres par tags */}
        <aside className="discover-sidebar">
          <div className="sidebar-section">
            <h3 className="sidebar-title">Filtrer par tags</h3>
            <div className="tags-list">
              {allTags.map((tag) => {
                const tagAccessible = isTagAccessible(tag.id)
                const requiredLevel = getTagRequiredLevel(tag.id)
                const isLocked = !tagAccessible && requiredLevel !== null
                
                return (
                  <button
                    key={tag.id}
                    className={`tag-button ${selectedTags.includes(tag.id) ? 'selected' : ''} ${isLocked ? 'locked' : ''}`}
                    onClick={() => toggleTag(tag.id)}
                    disabled={isLocked}
                  >
                    <span className="tag-name">{tag.name}</span>
                    {requiredLevel && (
                      <span className={`tag-badge ${requiredLevel}`}>
                        {requiredLevel === 'basic' && 'BASIC'}
                        {requiredLevel === 'premium' && 'PREMIUM'}
                        {requiredLevel === 'vip' && 'VIP'}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {selectedTags.length > 0 && (
            <div className="selected-tags-section">
              <div className="selected-tags-header">
                <span className="selected-count">{selectedTags.length} tag(s) sélectionné(s)</span>
                <button 
                  className="clear-all-button"
                  onClick={() => setSelectedTags([])}
                >
                  Tout effacer
                </button>
              </div>
              <div className="selected-tags-list">
                {selectedTags.map(tagId => {
                  const tag = allTags.find(t => t.id === tagId)
                  return tag ? (
                    <span key={tagId} className="selected-tag">
                      {tag.name}
                      <button onClick={() => toggleTag(tagId)} className="remove-tag">×</button>
                    </span>
                  ) : null
                })}
              </div>
            </div>
          )}
        </aside>

        {/* Contenu principal avec résultats */}
        <main className="discover-main">
          {isLoading && videos.length === 0 ? (
            <div className="discover-loading">
              <p>Chargement du contenu...</p>
            </div>
          ) : videos && videos.length > 0 ? (
            <>
              <div className="results-header">
                <h2>{selectedTags.length > 0 ? `Résultats` : 'Tous les contenus'}</h2>
              </div>
              <div className="videos-grid">
                {videos.map((video, videoIndex) => {
                  const isFirstVideos = videoIndex < 3
                  const hasPremiumAccess = hasAccessLevel('premium')
                  const hasVipAccess = hasAccessLevel('vip')
                  const hasAccess = isFirstVideos || hasVipAccess || (videoIndex >= 3 && hasPremiumAccess)
                  
                  return (
                    <div 
                      key={video.id} 
                      className={`video-card ${!hasAccess ? 'premium' : ''}`}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleVideoClick(video, videoIndex)
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="video-thumbnail-container">
                        {video.thumbnailS3Key ? (
                          <img
                            src={video.thumbnailS3Key}
                            alt={video.title}
                            className="video-thumbnail"
                          />
                        ) : (
                          <div className="video-thumbnail placeholder">
                            <div className="placeholder-content">
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          </div>
                        )}
                        
                        {!hasAccess && (
                          <div className="premium-badge">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 1l3 6h6l-5 5 2 6-6-3-6 3 2-6-5-5h6l3-6z"/>
                            </svg>
                            Premium
                          </div>
                        )}
                        
                      {!hasAccess && (
                          <div 
                            className="premium-overlay"
                            onClick={(e) => {
                              e.stopPropagation()
                            navigate(buildVideoRedirectUrl(video))
                            }}
                          >
                            <div className="lock-icon">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                              </svg>
                            </div>
                            <span>Abonnement requis</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="video-info">
                        <h3 className="video-title">{video.title}</h3>
                        {video.duration && (
                          <div className="video-meta">
                            <span className="video-duration">
                              {typeof video.duration === 'number' 
                                ? `${Math.floor(video.duration / 60)}:${(video.duration % 60).toString().padStart(2, '0')}`
                                : video.duration
                              }
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Infinite scroll trigger */}
              {canLoadMore && (
                <div ref={loadMoreRef} className="load-more-trigger">
                  {isLoading && <p>Chargement...</p>}
                </div>
              )}
              {!canLoadMore && videos.length > 0 && (accessLevel === null || accessLevel === 'basic') && (
                <div className="upgrade-prompt">
                  <p>Passez à un abonnement Premium ou VIP pour continuer à explorer plus de contenu</p>
                  <button className="upgrade-button" onClick={() => navigate('/subscriptions')}>
                    Voir les abonnements
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-results">
              <p>Aucun résultat trouvé</p>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default DiscoverPage
